import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  Alert,
  Drawer,
  IconButton,
  Divider,
  Badge,
  Stack,
  Chip,
  CircularProgress,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import DrawOutlinedIcon from "@mui/icons-material/DrawOutlined";
import SignatureBox from "../Signature/SignatureBox";
import { pageMobileCardSx } from "../../styles/pageModernStyles";
import {
  getDocumentosUsuario,
  saveFirmaBase64,
  firmarDocumentoIndividual,
  getEstadoFirmaDocumento,
} from "../../api/User/userApi";

const API_URL = process.env.REACT_APP_API_URL;

const BADGE_ACCENT = {
  primary: "#0288d1",
  error: "#d32f2f",
  warning: "#f57c00",
  success: "#2e7d32",
};

const JobCard = ({ name, email, open, onOpen, onClose }) => {
  const theme = useTheme();
  const userEmail = email.toLowerCase();

  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [firmaEstados, setFirmaEstados] = useState({});
  const [showSignatureBox, setShowSignatureBox] = useState(null);
  const token = useMemo(() => localStorage.getItem("token"), []);

  useEffect(() => {
    const loadDocs = async () => {
      const response = await getDocumentosUsuario(userEmail);
      setDocs(Array.isArray(response) ? response : []);
    };
    loadDocs();
  }, [userEmail]);

  useEffect(() => {
    if (docs.length === 0) return;

    const loadFirmas = async () => {
      const estados = {};
      for (const d of docs) {
        const res = await getEstadoFirmaDocumento(d.id);
        estados[d.id] = res;
      }
      setFirmaEstados(estados);
    };

    loadFirmas();
  }, [docs]);

  useEffect(() => {
    if (!open) {
      setShowSignatureBox(null);
      return;
    }

    const loadDocs = async () => {
      const response = await getDocumentosUsuario(userEmail);
      setDocs(Array.isArray(response) ? response : []);
    };

    loadDocs();
  }, [open, userEmail]);

  const badgeColor = useMemo(() => {
    const total = docs.length;
    if (total === 0) return "primary";

    const signedCount = Object.values(firmaEstados).filter(
      (f) => f?.firmado,
    ).length;

    if (signedCount === 0) return "error";
    if (signedCount < total) return "warning";
    return "success";
  }, [docs, firmaEstados]);

  const accent = BADGE_ACCENT[badgeColor] || theme.palette.primary.main;

  const handleFirmarDocumento = async (docId, base64) => {
    setLoading(true);

    try {
      await saveFirmaBase64(userEmail, base64);
      await firmarDocumentoIndividual(docId);

      const refreshed = {};
      for (const d of docs) {
        const res = await getEstadoFirmaDocumento(d.id);
        refreshed[d.id] = res;
      }
      setFirmaEstados(refreshed);
      setShowSignatureBox(null);
    } finally {
      setLoading(false);
    }
  };

  const signedCount = Object.values(firmaEstados).filter((f) => f?.firmado).length;

  return (
    <>
      <Box
        onClick={onOpen}
        sx={{
          ...pageMobileCardSx(theme, { accentColor: accent }),
          pl: 2,
          pr: 1.5,
          py: 1.5,
          cursor: "pointer",
          transition: "transform 0.15s ease, box-shadow 0.15s ease",
          "&:active": { transform: "scale(0.99)" },
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1.5,
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: alpha(accent, 0.12),
                color: accent,
              }}
            >
              <PersonOutlineOutlinedIcon sx={{ fontSize: 20 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography fontWeight={800} sx={{ fontSize: "0.95rem", lineHeight: 1.2 }} noWrap>
                {name}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 600, display: "block" }}
                noWrap
              >
                {email}
              </Typography>
            </Box>
          </Stack>
          <Badge
            badgeContent={docs.length}
            color={badgeColor}
            showZero
            sx={{
              flexShrink: 0,
              mr: 1,
              "& .MuiBadge-badge": {
                fontSize: "0.7rem",
                fontWeight: 800,
              },
            }}
          />
        </Stack>
      </Box>

      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: "min(720px, 94vw)" },
            maxWidth: "100%",
            bgcolor: theme.palette.background.default,
          },
        }}
      >
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 2,
            px: { xs: 1.5, sm: 2 },
            py: 1.5,
            bgcolor: theme.palette.background.paper,
            borderBottom: "1px solid",
            borderColor: "divider",
            boxShadow: `0 4px 16px ${alpha(theme.palette.common.black, 0.06)}`,
          }}
        >
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>
                Documentos
              </Typography>
              <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.2 }}>
                {name}
              </Typography>
              <Typography variant="body2" color="text.secondary" noWrap>
                {email}
              </Typography>
              <Stack direction="row" spacing={0.75} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                <Chip
                  size="small"
                  label={`${docs.length} docs`}
                  sx={{ fontWeight: 700, height: 22 }}
                />
                <Chip
                  size="small"
                  variant="outlined"
                  label={`${signedCount}/${docs.length} firmados`}
                  sx={{
                    fontWeight: 700,
                    height: 22,
                    borderColor: alpha(accent, 0.4),
                    color: accent,
                  }}
                />
              </Stack>
            </Box>
            <IconButton onClick={onClose} aria-label="Cerrar" edge="end">
              <CloseIcon />
            </IconButton>
          </Stack>
        </Box>

        <Box
          sx={{
            p: { xs: 1.5, sm: 2 },
            pb: { xs: 4, sm: 3 },
            overflow: "auto",
            flex: 1,
          }}
        >
          {docs.length === 0 ? (
            <Alert severity="info">No hay documentos asignados.</Alert>
          ) : null}

          {docs.map((doc, index) => (
            <Box key={doc.id} sx={{ mb: index < docs.length - 1 ? 3 : 0 }}>
              <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1 }}>
                {doc.nombreDocumento}
              </Typography>

              {token ? (
                <Box
                  sx={{
                    borderRadius: 2,
                    overflow: "hidden",
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: "#fff",
                    mb: 2,
                  }}
                >
                  <iframe
                    title={`Vista previa de ${doc.nombreDocumento}`}
                    src={`${API_URL}/api/user-documents/view-public/${doc.id}?token=${encodeURIComponent(token)}`}
                    width="100%"
                    style={{
                      border: 0,
                      display: "block",
                      height: "min(520px, 58vh)",
                      minHeight: 280,
                    }}
                  />
                </Box>
              ) : null}

              {firmaEstados[doc.id]?.firmado ? (
                <Stack spacing={1.5}>
                  <Alert severity="success" icon={<CheckCircleIcon />}>
                    Documento firmado correctamente.
                  </Alert>
                  <Button
                    variant="outlined"
                    component="a"
                    href={`${API_URL}/api/user-documents/${doc.id}/download-signed?token=${encodeURIComponent(token)}`}
                    sx={{ alignSelf: "flex-start", borderRadius: 2 }}
                  >
                    Descargar firmado
                  </Button>
                </Stack>
              ) : (
                <Stack spacing={1.5}>
                  {!showSignatureBox && (
                    <Button
                      variant="contained"
                      startIcon={<DrawOutlinedIcon />}
                      onClick={() => setShowSignatureBox(doc.id)}
                      disabled={loading}
                      sx={{ alignSelf: "flex-start", borderRadius: 2 }}
                    >
                      Firmar este documento
                    </Button>
                  )}

                  {showSignatureBox === doc.id ? (
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: "divider",
                        bgcolor: theme.palette.background.paper,
                      }}
                    >
                      {loading ? (
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                          <CircularProgress size={18} />
                          <Typography variant="body2">Guardando firma…</Typography>
                        </Stack>
                      ) : null}
                      <SignatureBox
                        onSave={(base64) => handleFirmarDocumento(doc.id, base64)}
                        onCancel={() => setShowSignatureBox(null)}
                      />
                    </Box>
                  ) : null}
                </Stack>
              )}

              {index < docs.length - 1 ? <Divider sx={{ mt: 3 }} /> : null}
            </Box>
          ))}
        </Box>
      </Drawer>
    </>
  );
};

export default JobCard;
